import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { ConfigService } from '@venecia/services';
import { UrlBuilder } from '@venecia/builders';
import { ApiResponse, RequestOptions, TemplateHelper } from '@venecia/apis/base';

import {
  AuthorizationDashboardActiveSessionsByApplication,
  AuthorizationDashboardActiveSessionsByApplicationRaw,
  AuthorizationDashboardActiveUsersByApplication,
  AuthorizationDashboardActiveUsersByApplicationRaw,
  AuthorizationDashboardAuthorizationCodesHourly,
  AuthorizationDashboardAuthorizationCodesHourlyRaw,
  AuthorizationDashboardFailedLoginRisk,
  AuthorizationDashboardFailedLoginRiskRaw,
  AuthorizationDashboardKpi,
  AuthorizationDashboardKpiRaw,
  AuthorizationDashboardOtpStatus,
  AuthorizationDashboardOtpStatusRaw,
  AuthorizationDashboardTokensRequestedByApplicationHourly,
  AuthorizationDashboardTokensRequestedByApplicationHourlyRaw,
  AuthorizationDashboardTokensRequestedHourly,
  AuthorizationDashboardTokensRequestedHourlyRaw,
} from './model/dashboard.model';

@Injectable({ providedIn: 'root' })
export class AuthorizationDashboardApi {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigService);

  private urlBuilder?: UrlBuilder;

  protected constructor() {
    this.configService
      .select('http.rest.auth.url')
      .pipe(take(1))
      .subscribe((value: string) => {
        this.urlBuilder = new UrlBuilder(value, '/api/dashboard');
      });
  }

  getActiveSessionsByApplication(): Observable<ApiResponse<AuthorizationDashboardActiveSessionsByApplication[]>> {
    return this.getByPath<AuthorizationDashboardActiveSessionsByApplicationRaw[]>('active-sessions-by-application').pipe(
      map((response) => ({
        ...response,
        body: response.body.map((item) => this.toActiveSessionsByApplication(item)),
      }))
    );
  }

  getActiveUsersByApplication(): Observable<ApiResponse<AuthorizationDashboardActiveUsersByApplication[]>> {
    return this.getByPath<AuthorizationDashboardActiveUsersByApplicationRaw[]>('active-users-by-application').pipe(
      map((response) => ({
        ...response,
        body: response.body.map((item) => this.toActiveUsersByApplication(item)),
      }))
    );
  }

  getTokensRequestedLast24hHourly(): Observable<ApiResponse<AuthorizationDashboardTokensRequestedHourly[]>> {
    return this.getByPath<AuthorizationDashboardTokensRequestedHourlyRaw[]>('tokens-requested-last-24h-hourly').pipe(
      map((response) => ({
        ...response,
        body: response.body.map((item) => this.toTokensRequestedHourly(item)),
      }))
    );
  }

  getTokensRequestedByApplicationLast24hHourly(): Observable<ApiResponse<AuthorizationDashboardTokensRequestedByApplicationHourly[]>> {
    return this.getByPath<AuthorizationDashboardTokensRequestedByApplicationHourlyRaw[]>('tokens-requested-by-application-last-24h-hourly').pipe(
      map((response) => ({
        ...response,
        body: response.body.map((item) => this.toTokensRequestedByApplicationHourly(item)),
      }))
    );
  }

  getKpis(): Observable<ApiResponse<AuthorizationDashboardKpi>> {
    return this.getByPath<AuthorizationDashboardKpiRaw>('kpis').pipe(
      map((response) => ({
        ...response,
        body: this.toKpi(response.body),
      }))
    );
  }

  getAuthorizationCodesLast24hHourly(): Observable<ApiResponse<AuthorizationDashboardAuthorizationCodesHourly[]>> {
    return this.getByPath<AuthorizationDashboardAuthorizationCodesHourlyRaw[]>('authorization-codes-last-24h-hourly').pipe(
      map((response) => ({
        ...response,
        body: response.body.map((item) => this.toAuthorizationCodesHourly(item)),
      }))
    );
  }

  getFailedLoginRisk(): Observable<ApiResponse<AuthorizationDashboardFailedLoginRisk[]>> {
    return this.getByPath<AuthorizationDashboardFailedLoginRiskRaw[]>('failed-login-risk').pipe(
      map((response) => ({
        ...response,
        body: response.body.map((item) => this.toFailedLoginRisk(item)),
      }))
    );
  }

  getOtpStatusLast24h(): Observable<ApiResponse<AuthorizationDashboardOtpStatus[]>> {
    return this.getByPath<AuthorizationDashboardOtpStatusRaw[]>('otp-status-last-24h').pipe(
      map((response) => ({
        ...response,
        body: response.body.map((item) => this.toOtpStatus(item)),
      }))
    );
  }

  getByPath<T>(path: string | number, options?: RequestOptions): Observable<ApiResponse<T>> {
    const url = this.urlBuilder!.build(path);
    const httpOptions = TemplateHelper.buildOptions(options);

    return this.http
      .get<ApiResponse<T>>(url, httpOptions)
      .pipe(
        TemplateHelper.applyRetry(options),
        catchError((err) => TemplateHelper.handleError(err))
      );
  }

  private toActiveSessionsByApplication(
    source: AuthorizationDashboardActiveSessionsByApplicationRaw
  ): AuthorizationDashboardActiveSessionsByApplication {
    return {
      applicationRef: source['application-ref'],
      activeSessions: source['active-sessions'],
      activeUsers: source['active-users'],
    };
  }

  private toActiveUsersByApplication(
    source: AuthorizationDashboardActiveUsersByApplicationRaw
  ): AuthorizationDashboardActiveUsersByApplication {
    return {
      applicationRef: source['application-ref'],
      activeUsers: source['active-users'],
    };
  }

  private toTokensRequestedHourly(
    source: AuthorizationDashboardTokensRequestedHourlyRaw
  ): AuthorizationDashboardTokensRequestedHourly {
    return {
      requestedHour: source['requested-hour'],
      requestedTokens: source['requested-tokens'],
      accessTokens: source['access-tokens'],
      refreshTokens: source['refresh-tokens'],
      idTokens: source['id-tokens'],
    };
  }

  private toTokensRequestedByApplicationHourly(
    source: AuthorizationDashboardTokensRequestedByApplicationHourlyRaw
  ): AuthorizationDashboardTokensRequestedByApplicationHourly {
    return {
      requestedHour: source['requested-hour'],
      applicationRef: source['application-ref'],
      requestedTokens: source['requested-tokens'],
      accessTokens: source['access-tokens'],
      refreshTokens: source['refresh-tokens'],
      idTokens: source['id-tokens'],
    };
  }

  private toKpi(source: AuthorizationDashboardKpiRaw): AuthorizationDashboardKpi {
    return {
      activeClients: source['active-clients'],
      availableAccounts: source['available-accounts'],
      activeSessions: source['active-sessions'],
      activeAccessTokens: source['active-access-tokens'],
    };
  }

  private toAuthorizationCodesHourly(
    source: AuthorizationDashboardAuthorizationCodesHourlyRaw
  ): AuthorizationDashboardAuthorizationCodesHourly {
    return {
      requestedHour: source['requested-hour'],
      createdCodes: source['created-codes'],
      consumedCodes: source['consumed-codes'],
      expiredUnconsumedCodes: source['expired-unconsumed-codes'],
    };
  }

  private toFailedLoginRisk(source: AuthorizationDashboardFailedLoginRiskRaw): AuthorizationDashboardFailedLoginRisk {
    return {
      username: source.username,
      failedAttempts: source['failed-attempts'],
      maxFailedAttempts: source['max-failed-attempts'],
      lastFailedAttemptAt: source['last-failed-attempt-at'],
      accessBlockedAt: source['access-blocked-at'],
    };
  }

  private toOtpStatus(source: AuthorizationDashboardOtpStatusRaw): AuthorizationDashboardOtpStatus {
    return {
      purpose: source.purpose,
      status: source.status,
      total: source.total,
    };
  }

}
